import React, { useMemo, useState } from 'react';
import {
  Box,
  Text,
  Link,
  QuickFilter,
  QuickFilterGroup,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  ChevronDownIcon,
  ChevronUpIcon,
  SelectInput,
  CloseIcon,
  IconButton,
  Skeleton,
  useToast,
} from '@razorpay/blade/components';
import { ClaimsFiltersProps, ClaimFilterType } from './types';
import { claimFilters, claimFiltersMap } from './constants';
import { useFilters } from 'hooks/useFilters';
import { useQuery } from 'react-query';
import api from 'api';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useSelector } from 'react-redux';
import { FlexibleBenefitsComponent } from '../types';
import { FlexibleBenefitAssignedComponent } from './Create/types';
import { useSearchParams } from 'hooks/useSearchParams';

export const ClaimsFilters = ({ counts }: ClaimsFiltersProps) => {
  const [showAllFilters, setShowAllFilters] = useState(false);
  const { currentFilters, applyFilter, removeFilter } = useFilters({
    filtersMap: claimFiltersMap,
    multipleValueFilters: [claimFiltersMap.benefitType],
  });

  const handleShowAllFilters = () => {
    setShowAllFilters((prev) => !prev);
  };

  const benefitTypes =
    currentFilters?.benefitType.length > 0 ? currentFilters?.benefitType?.[0]?.split(',') : [];

  const handleBenefitTypeChange = (values: string[]) => {
    if (values.length === 0) {
      removeFilter(claimFiltersMap.benefitType);
    } else {
      applyFilter(claimFiltersMap.benefitType, values.join(','));
    }
  };

  const handleClearAll = () => {
    removeFilter(claimFiltersMap.benefitType);
  };

  const selectedStatus = (currentFilters.status as ClaimFilterType) || 'all';

  const { urlParams } = useSearchParams();
  const activeUserId = useSelector(loggedInUserSelectors.peopleId);
  const userId = urlParams.get('userId') || activeUserId;

  const toast = useToast();

  const payrollListAssignedComponents = useQuery({
    queryKey: ['payrollListAssignedComponents', userId],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexibleBenefitAssignedComponents({
        user_id: userId?.toString() || '',
        limit: 100,
        offset: 0,
      }),
    select: (data) => data.payrollListAssignedComponents.data,
    onError: (error) => {
      toast.show({
        content: 'Failed to load assigned cash benefits, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
    enabled: !!userId,
  });

  const { data: flexibleBenefitsListData, isLoading: isLoadingFlexibleBenefitsList } = useQuery({
    queryKey: ['flexibleComponentListData', userId],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexibleBenefitsList({
        offset: 0,
        limit: 100,
      }),
    select: (data) => data.payrollListFlexibleBenefitsComponents.data,
    onError: (error) => {
      toast.show({
        content: 'Failed to load flexible components list, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
    enabled: !!userId,
  });

  const assignedCashBenefits = useMemo(() => {
    if (!payrollListAssignedComponents?.data || !flexibleBenefitsListData) return [];
    return payrollListAssignedComponents?.data?.filter(
      (component: FlexibleBenefitAssignedComponent) => {
        return (
          flexibleBenefitsListData?.find(
            (benefit: FlexibleBenefitsComponent) => benefit.id === component.component_id,
          )?.settings.flexible_settings?.salary_settings?.non_cash_benefit === false
        );
      },
    );
  }, [flexibleBenefitsListData, payrollListAssignedComponents.data]);

  const getSelectedBenefitNames = () => {
    const componentNames = benefitTypes.map((value: string) => {
      const component = assignedCashBenefits?.find((type) => type.component_id === value);
      return component?.component_name;
    });
    return [...new Set(componentNames)].join(', ');
  };

  const isLoading = payrollListAssignedComponents.isLoading || isLoadingFlexibleBenefitsList;

  return (
    <Box display="flex" flexDirection="column">
      <Box
        display="flex"
        flexDirection={{ base: 'column', m: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', m: 'center' }}
        paddingY="spacing.5"
        paddingX="spacing.0">
        <QuickFilterGroup
          selectionType="single"
          value={selectedStatus}
          onChange={({ values }) => applyFilter(claimFiltersMap.status, values[0])}>
          <Box display="flex" gap="spacing.4" alignItems="center" flexWrap="wrap">
            <Text color="surface.text.gray.muted" weight="semibold">
              Filters :
            </Text>
            {claimFilters.map((filter) => (
              <QuickFilter
                key={filter.key}
                value={filter.key}
                title={filter.label}
              />
            ))}
          </Box>
        </QuickFilterGroup>
        <Link
          variant="button"
          onClick={handleShowAllFilters}
          icon={showAllFilters ? ChevronUpIcon : ChevronDownIcon}
          iconPosition="right">
          {showAllFilters ? 'View less' : 'View more'}
        </Link>
      </Box>

      {showAllFilters &&
        (isLoading ? (
          <Skeleton width="200px" height="36px" />
        ) : (
          <Box display="flex" flexDirection="column" gap="spacing.3" paddingBottom="spacing.3">
            <Box minWidth="200px" maxWidth={'300px'}>
              <Dropdown selectionType="multiple">
                <SelectInput
                  placeholder="Select Benefit Type"
                  accessibilityLabel="benefit type filter"
                  value={benefitTypes}
                  onChange={({ values }) => handleBenefitTypeChange(values)}
                />
                <DropdownOverlay>
                  <ActionList>
                    {(assignedCashBenefits || [])?.map(
                      ({ component_display_name = '', component_id = '' }) => (
                        <ActionListItem
                          key={component_id}
                          value={component_id}
                          title={component_display_name}
                        />
                      ),
                    )}
                  </ActionList>
                </DropdownOverlay>
              </Dropdown>
            </Box>

            {benefitTypes.length > 0 && (
              <Box
                display="flex"
                flexDirection="row"
                gap="spacing.5"
                alignItems="center"
                paddingY="spacing.2">
                <Box
                  display="flex"
                  flexDirection="row"
                  gap="spacing.3"
                  alignItems="center"
                  paddingY="spacing.2"
                  paddingX="spacing.3"
                  backgroundColor="surface.background.primary.subtle"
                  borderColor="surface.border.primary.normal"
                  borderWidth="thin"
                  borderRadius="small">
                  <Text size="small" weight="semibold">
                    Pay type:
                  </Text>
                  <Text size="small" weight="regular">
                    {getSelectedBenefitNames()}
                  </Text>
                  <IconButton
                    icon={CloseIcon}
                    onClick={() => removeFilter(claimFiltersMap.benefitType)}
                    accessibilityLabel="close button"
                  />
                </Box>
                <Link size="small" variant="button" onClick={handleClearAll}>
                  Clear all
                </Link>
              </Box>
            )}
          </Box>
        ))}
    </Box>
  );
};
